import { z } from 'zod'

export const LEARNING_LEARNER_CATALOG_CONTRACT_VERSION = '1.0.0' as const
export const LEARNING_LEARNER_CATALOG_SCHEMA_VERSION = 'learning-learner-catalog-v1' as const
export const LEARNING_LEARNER_CATALOG_PAGE_SIZE = 24 as const

const text = z.string().trim().min(1)
const queryText = z.string().trim().max(200)
const imageUrl = z.string().trim().min(1).max(2048).refine(
  (value) => value.startsWith('/') && !value.startsWith('//') || /^https:\/\//u.test(value),
  'imageUrl must be HTTPS or same-origin relative',
)

export const learningLearnerCatalogQuerySchema = z.object({
  q: queryText.default(''),
  type: z.enum(['all', 'program', 'course', 'module']).default('all'),
  topic: queryText.default(''),
  status: z.enum(['all', 'unknown', 'not_started', 'in_progress', 'completed']).default('all'),
  access: z.enum(['all', 'available', 'locked']).default('all'),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
}).strict()

export const learningLearnerCatalogEntrySchema = z.object({
  id: text.max(240),
  nodeId: text.max(180),
  type: z.enum(['program', 'course', 'module']),
  title: text.max(240),
  description: z.string().trim().max(1000).nullable(),
  tags: z.array(text.max(120)).max(100),
  imageUrl: imageUrl.optional(),
  courseId: text.max(180).optional(),
  moduleSlug: text.max(180).optional(),
  programId: text.max(180).optional(),
  canSee: z.literal(true),
  canAccess: z.boolean(),
  accessReason: z.enum(['ENTITLEMENT_REQUIRED', 'CONTENT_UNAVAILABLE']).nullable(),
  status: z.enum(['unknown', 'not_started', 'in_progress', 'completed']),
}).strict()

export const learningLearnerCatalogFacetsSchema = z.object({
  types: z.record(z.string(), z.number().int().nonnegative()),
  topics: z.record(z.string(), z.number().int().nonnegative()),
  statuses: z.record(z.string(), z.number().int().nonnegative()),
  access: z.record(z.string(), z.number().int().nonnegative()),
}).strict()

export const learningLearnerCatalogResponseSchema = z.object({
  items: z.array(learningLearnerCatalogEntrySchema).max(72),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.literal(LEARNING_LEARNER_CATALOG_PAGE_SIZE),
  facets: learningLearnerCatalogFacetsSchema,
  query: learningLearnerCatalogQuerySchema,
}).strict()

export type LearningLearnerCatalogQuery = z.infer<typeof learningLearnerCatalogQuerySchema>
export type LearningLearnerCatalogQueryInput = z.input<typeof learningLearnerCatalogQuerySchema>
export type LearningLearnerCatalogEntry = z.infer<typeof learningLearnerCatalogEntrySchema>
export type LearningLearnerCatalogFacets = z.infer<typeof learningLearnerCatalogFacetsSchema>
export type LearningLearnerCatalogResponse = z.infer<typeof learningLearnerCatalogResponseSchema>

// PascalCase aliases match the Mac learner surface while the generated family uses
// the repository's lower-camel schema naming convention.
export const LearnerCatalogQuerySchema = learningLearnerCatalogQuerySchema
export const LearnerCatalogEntrySchema = learningLearnerCatalogEntrySchema
export const LearnerCatalogResponseSchema = learningLearnerCatalogResponseSchema
export type LearnerCatalogQuery = LearningLearnerCatalogQuery
export type LearnerCatalogEntry = LearningLearnerCatalogEntry
export type LearnerCatalogResponse = LearningLearnerCatalogResponse
